/** Policies: the client side of the portal's legal API (requirements, reading, acceptance). */
import { authedPortal } from "./api";
import { PORTAL, TERMS_REQUIRED, VERSION_NOT_CURRENT, termsRequired } from "./legal-core";

export { PORTAL, TERMS_REQUIRED, VERSION_NOT_CURRENT, termsRequired };

export type PolicyFormat = "pdf" | "docx" | "html";

export interface Requirement {
  document_id: string;
  title: string;
  description: string | null;
  version_id: string;
  version_code: string | null;
  version_number: number;
  effective_at: string | null;
  format: PolicyFormat;
  change_summary: string | null;
  accepted: boolean;
  accepted_version_code: string | null;
  accepted_at: string | null;
  blocking: boolean;
  reason: "new" | "updated" | null;
}

export interface Requirements {
  point: string;
  clear: boolean;
  documents: Requirement[];
}

export interface PolicySummary {
  document_id: string;
  title: string;
  slug: string | null;
  version_id: string;
  version_number: number;
  version_code: string | null;
  published_at: string;
  format: PolicyFormat;
}

export interface ReadablePolicy {
  version_id: string;
  code: string | null;
  title: string;
  format: PolicyFormat;
  html: string | null;
  version_number: number;
  published_at: string;
}

export interface PolicyAcceptance {
  id: string;
  version_id: string;
  document_id: string;
  accepted_at: string;
}

/** What still blocks the person: blocking and not yet accepted, in the server's order. */
export function outstanding(r: Requirements): Requirement[] {
  return r.documents.filter((d) => d.blocking && !d.accepted);
}

function legalHeaders(orgId: string | null): Record<string, string> {
  return orgId ? { "X-CortexGrip-Organization": orgId } : {};
}

function bytesOf(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); // environments whose Blob has no arrayBuffer()
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

export const legal = {
  requirements: (orgId: string | null) =>
    authedPortal<Requirements>("/v1/legal/requirements", { headers: legalHeaders(orgId) }),
  policies: (orgId: string | null) =>
    authedPortal<{ policies: PolicySummary[] }>("/v1/legal/policies", { headers: legalHeaders(orgId) }),
  version: (versionId: string, orgId: string | null) =>
    authedPortal<ReadablePolicy>(`/v1/legal/versions/${encodeURIComponent(versionId)}`, {
      headers: legalHeaders(orgId),
    }),
  file: async (versionId: string, orgId: string | null): Promise<ArrayBuffer> => {
    const blob = await authedPortal<Blob>(`/v1/legal/versions/${encodeURIComponent(versionId)}/file`, {
      headers: legalHeaders(orgId),
      responseType: "blob",
    });
    return bytesOf(blob);
  },
  accept: (versionId: string, orgId: string | null) =>
    authedPortal<PolicyAcceptance>(`/v1/legal/versions/${encodeURIComponent(versionId)}/accept`, {
      method: "POST",
      body: { agree: true, ...(orgId ? { organization_id: orgId } : {}) },
      headers: legalHeaders(orgId),
    }),
};

const ALLOWED_TAGS = new Set(["A", "BR", "EM", "H2", "H3", "LI", "OL", "P", "STRONG", "UL"]);

/** The API returns sanitised HTML, but keep a client-side boundary before rendering rich text. */
export function sanitizeLegalHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = parsed.body.firstElementChild;
  if (!root) return "";
  const walk = (node: Element) => {
    for (const child of [...node.children]) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        child.replaceWith(...child.childNodes);
        continue;
      }
      for (const attribute of [...child.attributes]) {
        if (child.tagName === "A" && attribute.name === "href") {
          const value = attribute.value.trim();
          if (/^(https?:|mailto:|\/|#)/i.test(value)) continue;
        }
        child.removeAttribute(attribute.name);
      }
      if (child.tagName === "A" && child.hasAttribute("href")) {
        child.setAttribute("rel", "noreferrer noopener");
        child.setAttribute("target", "_blank");
      }
      walk(child);
    }
  };
  walk(root);
  return root.innerHTML;
}
